import React from 'react';
import {
  Globe, Mail, Linkedin, Instagram, Facebook, MessageSquare, Phone,
  Briefcase, Heart, Star, Coffee, Music, Camera, Github, Youtube,
  Twitter, Twitch, Download, Check, Link as LinkIcon
} from 'lucide-react';

const ICON_MAP = {
  globe: Globe, mail: Mail, linkedin: Linkedin, instagram: Instagram,
  facebook: Facebook, 'message-square': MessageSquare, phone: Phone,
  link: LinkIcon, briefcase: Briefcase, heart: Heart, star: Star,
  coffee: Coffee, music: Music, camera: Camera, github: Github, youtube: Youtube,
  twitter: Twitter, twitch: Twitch, tiktok: Globe, discord: Globe,
  threads: Globe, mastodon: Globe, snapchat: Globe, video: Camera,
};

const getYouTubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : '';
};

const getVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : '';
};

const LinksContent = ({ profile, theme, themeVars, onLinkClick, savedContact, onDownloadVCard }) => {
  const t = theme;
  const { subtleBg, subtleBorder, mutedText, isDark } = themeVars;

  return (
    <div data-testid="public-links-section">
      <button onClick={onDownloadVCard}
        className="w-full py-3.5 rounded-full font-bold uppercase tracking-wider text-sm transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 mb-8"
        style={{
          background: savedContact ? 'transparent' : t.primary_color,
          color: savedContact ? t.primary_color : (isDark ? t.bg_color : '#ffffff'),
          border: `2px solid ${t.primary_color}`
        }}
        data-testid="public-save-contact-button">
        {savedContact ? <><Check className="w-4 h-4" /> Saved</> : <><Download className="w-4 h-4" /> Save Contact</>}
      </button>

      <div className="grid grid-cols-3 gap-3" data-testid="public-links-grid">
        {profile.links.map((link, idx) => {
          const Icon = ICON_MAP[link.icon] || Globe;
          return (
            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
              onClick={() => onLinkClick(link.name, link.url)}
              className="flex flex-col items-center gap-2.5 group" data-testid={`public-link-${idx}`}>
              <div className="w-[72px] h-[72px] rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105 group-active:scale-95"
                style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
                <Icon className="w-6 h-6" style={{ color: t.primary_color }} />
              </div>
              <span className="text-[11px] font-medium text-center leading-tight max-w-[72px]" style={{ color: mutedText }}>
                {link.name}
              </span>
            </a>
          );
        })}
      </div>

      {/* Embeds Section */}
      {profile.embeds && profile.embeds.length > 0 && (
        <div className="mt-8 space-y-4" data-testid="public-embeds">
          {profile.embeds.map((embed, idx) => (
            <div key={idx} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${subtleBorder}` }} data-testid={`public-embed-${idx}`}>
              {embed.title && <p className="text-xs font-semibold px-3 py-2" style={{ color: mutedText, background: subtleBg }}>{embed.title}</p>}
              <div className="aspect-video bg-black">
                {embed.embed_type === 'youtube' && embed.embed_url && (
                  <iframe src={`https://www.youtube.com/embed/${getYouTubeId(embed.embed_url)}`}
                    className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={embed.title || 'YouTube'} />
                )}
                {embed.embed_type === 'vimeo' && embed.embed_url && (
                  <iframe src={`https://player.vimeo.com/video/${getVimeoId(embed.embed_url)}`}
                    className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={embed.title || 'Vimeo'} />
                )}
                {embed.embed_type === 'spotify' && embed.embed_url && (
                  <iframe src={embed.embed_url.replace('open.spotify.com', 'open.spotify.com/embed')}
                    className="w-full h-full" frameBorder="0" allow="encrypted-media" title={embed.title || 'Spotify'} />
                )}
                {(embed.embed_type === 'custom' || embed.embed_type === 'map') && embed.embed_url && (
                  <iframe src={embed.embed_url} className="w-full h-full" frameBorder="0" title={embed.title || 'Embed'} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Showcase Section */}
      {profile.showcase && profile.showcase.length > 0 && (
        <div className="mt-8" data-testid="public-showcase">
          <p className="text-xs font-semibold uppercase tracking-wider mb-4 text-center" style={{ color: mutedText }}>Products & Services</p>
          <div className="space-y-3">
            {profile.showcase.map((item, idx) => (
              <a key={idx} href={item.link_url || '#'} target={item.link_url ? '_blank' : undefined} rel="noopener noreferrer"
                className="flex gap-3 p-3 rounded-xl transition-transform hover:scale-[1.02]"
                style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}
                data-testid={`public-showcase-${idx}`}>
                {item.image_url && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: t.text_color }}>{item.title}</p>
                  {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: mutedText }}>{item.description}</p>}
                  {item.price && <p className="text-sm font-bold mt-2" style={{ color: t.primary_color }}>{item.price}</p>}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      {profile.testimonials && profile.testimonials.length > 0 && (
        <div className="mt-8" data-testid="public-testimonials">
          <p className="text-xs font-semibold uppercase tracking-wider mb-4 text-center" style={{ color: mutedText }}>What People Say</p>
          <div className="space-y-3">
            {profile.testimonials.map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }} data-testid={`public-testimonial-${idx}`}>
                <div className="flex items-center gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-3 h-3 ${s <= item.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ color: t.text_color }}>"{item.text}"</p>
                <div>
                  <p className="text-xs font-semibold" style={{ color: t.text_color }}>{item.name}</p>
                  {item.role && <p className="text-[11px]" style={{ color: mutedText }}>{item.role}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LinksContent;
